import type { PlateOptimisationData } from "../components/common/PlateOptimisationCard";

export const GLUCERA_PLATE_OPTIMISATION_KEY = "glucera_plate_optimisation";

type StoredPlatePayload = {
  data: PlateOptimisationData;
  mealId?: string;
  savedAt: string;
};

const isPlateOptimisationData = (
  value: unknown
): value is PlateOptimisationData => {
  if (!value || typeof value !== "object") return false;
  const p = value as PlateOptimisationData;
  if (typeof p.headline !== "string" || typeof p.subtext !== "string") {
    return false;
  }
  const steps =
    p.tip_type === "eating_order"
      ? p.steps ?? p.ordering_steps
      : p.ordering_steps ?? p.steps;
  return Array.isArray(steps) && steps.length > 0;
};

export const extractPlateOptimisationFromRecord = (
  parsed: Record<string, unknown> | null | undefined
): PlateOptimisationData | undefined => {
  if (!parsed) return undefined;

  const dataBlock = parsed.data as Record<string, unknown> | undefined;
  const resultBlock = parsed.result as Record<string, unknown> | undefined;
  const dataResult = dataBlock?.result as Record<string, unknown> | undefined;

  const candidates: unknown[] = [
    parsed.plate_optimisation,
    dataBlock?.plate_optimisation,
    resultBlock?.plate_optimisation,
    dataResult?.plate_optimisation,
  ];

  for (const candidate of candidates) {
    if (isPlateOptimisationData(candidate)) {
      return candidate;
    }
  }
  return undefined;
};

export const persistPlateOptimisation = (
  data: PlateOptimisationData,
  mealId?: string
): void => {
  try {
    const payload: StoredPlatePayload = {
      data,
      mealId,
      savedAt: new Date().toISOString(),
    };
    localStorage.setItem(
      GLUCERA_PLATE_OPTIMISATION_KEY,
      JSON.stringify(payload)
    );
    window.dispatchEvent(new CustomEvent("glucera-plate-optimisation-updated"));
  } catch {
    /* ignore quota / private mode */
  }
};

export const readStoredPlateOptimisation = (): PlateOptimisationData | undefined => {
  try {
    const raw = localStorage.getItem(GLUCERA_PLATE_OPTIMISATION_KEY);
    if (!raw) return undefined;
    const parsed = JSON.parse(raw) as StoredPlatePayload | PlateOptimisationData;

    if (isPlateOptimisationData(parsed)) {
      return parsed;
    }

    const stored = parsed as StoredPlatePayload;
    if (!isPlateOptimisationData(stored?.data)) return undefined;
    return stored.data;
  } catch {
    return undefined;
  }
};

export const readPlateOptimisationFromAnalyzeCache = (): PlateOptimisationData | undefined => {
  try {
    const raw = localStorage.getItem("glucera_analyze_response");
    if (!raw) return undefined;
    return extractPlateOptimisationFromRecord(
      JSON.parse(raw) as Record<string, unknown>
    );
  } catch {
    return undefined;
  }
};

export const resolvePlateOptimisationData = (options: {
  homePlate?: PlateOptimisationData;
}): PlateOptimisationData | undefined => {
  if (isPlateOptimisationData(options.homePlate)) {
    return options.homePlate;
  }

  const fromStore = readStoredPlateOptimisation();
  if (fromStore) return fromStore;

  return readPlateOptimisationFromAnalyzeCache();
};
